import { useState, useEffect, useMemo } from 'react'
import {
    View,
    Text,
    TouchableOpacity,
    StyleSheet,
    Platform,
    ActivityIndicator,
    ScrollView,
} from 'react-native'
import { LegendList } from '@legendapp/list';
import { Link, usePathname } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import type { Session } from '@supabase/supabase-js'
import PaperItem from './PaperItem'
import Header from './Header';
import Footer from './Footer'
import { useAuth } from '../lib/AuthContext';
import { useSubscription } from '../lib/SubscriptionContext';
import { useUserPapersQuery } from '../lib/hooks/useUserPapersQuery';
import { usePublicPapersInfiniteQuery } from '../lib/hooks/usePublicPapersInfiniteQuery';
import { canAccessMainApp } from '../utils/authHelpers';
import colors from '../lib/colors';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import {
    useOptionalResolvedMessage,
} from '../lib/i18n/messages';

const PUBLIC_FEED = msg({ id: 'main.publicFeed', message: 'Paperzilla Public Feed' });

interface MainScreenProps {
    session: Session | null,
    initialPublicPapers?: any[]
}

export default function MainScreen({ session, initialPublicPapers = [] }: MainScreenProps) {
    const { i18n } = useLingui();
    const { confirmationMessage, clearConfirmation } = useAuth();
    const confirmationTitle = useOptionalResolvedMessage(confirmationMessage?.title);
    const confirmationText = useOptionalResolvedMessage(confirmationMessage?.message);
    const [showConnectionNotice, setShowConnectionNotice] = useState(false);
    const pathname = usePathname();

    // Check if we're in SSG/SSR mode
    const isSSG = Platform.OS === 'web' && typeof window === 'undefined';
    const isClient = !isSSG;

    const [isHydrated, setIsHydrated] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [scrollViewOpacity, setScrollViewOpacity] = useState(1);

    // Hydration effect - start transition when client is ready
    useEffect(() => {
        if (isClient && !isHydrated) {
            setIsHydrated(true);

            console.log('🚀 [MainScreen] Hydration complete');

            // Start transition after a brief delay to let LegendList render
            setIsTransitioning(true);
            setTimeout(() => {
                // Fade out ScrollView over 200ms
                const startTime = Date.now();
                const duration = 200;
                
                const animate = () => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const opacity = 1 - progress;
                    
                    setScrollViewOpacity(opacity);
                    
                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    } else {
                        // Animation complete
                        setIsTransitioning(false);
                    }
                };
                
                requestAnimationFrame(animate);
            }, 100); // Wait 100ms for LegendList to render
        }
    }, [isClient, isHydrated]);

    // Fetch user papers (only if logged in and on client)
    const { data: userPapers } = useUserPapersQuery(
        isHydrated && canAccessMainApp(session) ? session?.user?.id : undefined
    );

    // Fetch public papers with invisible transition from static to live
    const {
        data: publicData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = usePublicPapersInfiniteQuery(
        isHydrated ? session?.user?.id : undefined,
        initialPublicPapers,
        isHydrated
    );

    // Get global subscription status
    const { subscriptionStatus, retrySubscription } = useSubscription();
    const isSubscriptionReady = subscriptionStatus === 'connected';

    // SMOOTH TRANSITION LOGIC - works the same regardless of list component
    const publicPapers = useMemo(() => {
        // Always start with static papers (SSG or initial client render)
        if (!isHydrated || !publicData?.pages?.length) {
            return initialPublicPapers;
        }

        // Seamlessly merge when live data arrives
        const paperMap = new Map();

        // Add live papers first (freshest data)
        publicData.pages.forEach(page => {
            page.papers.forEach(paper => {
                paperMap.set(paper.id, paper);
            });
        });

        // Add any static papers not in live data
        initialPublicPapers.forEach(paper => {
            if (!paperMap.has(paper.id)) {
                paperMap.set(paper.id, paper);
            }
        });

        // Return sorted array (newest first)
        return Array.from(paperMap.values()).sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }, [publicData, initialPublicPapers, isHydrated]);

    // Build list data with deduplication
    const listData = useMemo(() => {
        const data: any[] = [];
        const seenPaperIds = new Set<string>();

        // Add "My Papers" section only if user has papers
        if (isHydrated && userPapers && userPapers.length > 0) {
            data.push({ itemType: 'header', title: i18n._(msg({ id: 'main.myPapers', message: 'My Papers' })) });
            userPapers.forEach(p => {
                if (!seenPaperIds.has(p.id)) {
                    seenPaperIds.add(p.id);
                    data.push({ itemType: 'paper', paper: p });
                }
            });
        }

        // Always add public papers
        const filteredPublicPapers = publicPapers.filter(p => !seenPaperIds.has(p.id));

        if (filteredPublicPapers.length > 0) {
            data.push({ itemType: 'header', title: i18n._(PUBLIC_FEED) });
            filteredPublicPapers.forEach(p => {
                data.push({ itemType: 'paper', paper: p });
            });
        }

        return data;
    }, [userPapers, publicPapers, isHydrated, i18n]);

    // Handle confirmation messages from AuthContext
    useEffect(() => {
        if (!isClient) return;
        if (confirmationMessage && confirmationMessage.type === 'email_confirmed') {
            const timer = setTimeout(() => {
                clearConfirmation();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [confirmationMessage, clearConfirmation, isClient]);

    // Show connection notice after 3 seconds if not connected
    useEffect(() => {
        if (!isClient) return;
        let timer: ReturnType<typeof setTimeout>;
        if (!isSubscriptionReady && session) {
            timer = setTimeout(() => {
                setShowConnectionNotice(true);
            }, 3000);
        } else {
            setShowConnectionNotice(false);
        }
        return () => clearTimeout(timer);
    }, [isSubscriptionReady, session, isClient]);

    const renderItem = ({ item }: { item: any }) => {
        let isOwner;
        switch (item.itemType) {
            case 'header':
                if (item.title === PUBLIC_FEED) {
                    return (
                        <View style={styles.contentWrapper}>
                            <View style={styles.headerRow}>
                                <Text
                                    style={styles.headerRowTitle}
                                    accessibilityRole="header"
                                    {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                                >
                                    {item.title}
                                </Text>
                                <Link href="/fields" asChild>
                                    <TouchableOpacity style={styles.browseButton}>
                                        <Text style={styles.browseButtonText}><Trans id="mainscreen.browse" comment="UI copy in components/MainScreen.tsx">Browse</Trans></Text>
                                    </TouchableOpacity>
                                </Link>
                            </View>
                        </View>
                    );
                }
                // Render a title for the section
                return (
                    <View style={styles.contentWrapper}>
                        <Text style={styles.resultsTitle}>{item.title}</Text>
                    </View>
                );
            case 'paper':
                // Render the actual paper item
                isOwner = session?.user?.id ? item.paper.user_id === session.user.id : false;
                return (
                    <View style={styles.contentWrapper}>
                        <View style={styles.paperWrapper}>
                            <PaperItem item={item.paper} isOwner={isOwner} showPrivacyToggle={false} />
                        </View>
                    </View>
                );
            default:
                return null;
        }
    };

    const ListHeaderComponent = () => (
        <View style={styles.contentWrapper}>
            <View style={styles.archiveNotice} testID="legacy-upload-retired">
                <Text style={styles.archiveNoticeText}>
                    <Trans id="mainscreen.uploadsRetired" comment="Legacy PDF upload retirement notice; existing paper results and downloads remain available.">
                        PDF uploads are no longer available. Your existing papers remain accessible.
                    </Trans>
                </Text>
            </View>
            {showConnectionNotice && (
                <View style={styles.connectionNoticeCard}>
                    <View style={styles.connectionNoticeContent}>
                        <View style={styles.connectionNoticeInfo}>
                            <MaterialIcons name="info-outline" size={20} color={colors.primaryGreen} style={styles.connectionNoticeIcon} />
                            <Text style={styles.connectionNoticeText}><Trans id="mainscreen.liveUpdatesUnavailable" comment="UI copy in components/MainScreen.tsx">Live updates unavailable</Trans></Text>
                        </View>
                        <TouchableOpacity onPress={retrySubscription} style={styles.refreshButton}>
                            <MaterialIcons name="refresh" size={20} color={colors.primaryGreenLight} />
                        </TouchableOpacity>
                    </View>
                </View>
            )}
        </View>
    );

    const ListFooterComponent = () => (
        <>
            {isFetchingNextPage && (
                <View style={styles.contentWrapper}>
                    <View style={styles.footerLoader}>
                        <ActivityIndicator size="small" color={colors.primaryGreen} />
                        <Text style={styles.loadingMoreText}><Trans id="mainscreen.loadingMore" comment="UI copy in components/MainScreen.tsx">Loading more...</Trans></Text>
                    </View>
                </View>
            )}
            <Footer />
        </>
    );

    // One tree for SSR and initial hydration. The static papers stay visible
    // until the client list mounts; cached/private data enters only afterward.
    return (
        <View style={styles.page} testID="public-paper-feed"
            {...(Platform.OS === 'web' && { dataSet: { hydrationReady: String(isHydrated) } })}>
            <Header />
            {confirmationMessage && (
                <View style={styles.confirmationBanner}>
                    <View style={styles.confirmationContent}>
                        <Text style={styles.confirmationTitle2}>{confirmationTitle}</Text>
                        <Text style={styles.confirmationText}>{confirmationText}</Text>
                    </View>
                    <TouchableOpacity onPress={clearConfirmation} style={styles.confirmationClose}>
                        <Text style={styles.confirmationCloseText}>✕</Text>
                    </TouchableOpacity>
                </View>
            )}
            
            {/* List transition area - keep Header above */}
            <View style={{ flex: 1, position: 'relative' }}>
                {/* LegendList underneath (renders when hydrated) */}
                {isHydrated && (
                    <LegendList
                        key={`papers-list-${pathname === '/' ? 'home' : 'away'}`} 
                        data={listData}
                        renderItem={renderItem}
                        keyExtractor={(item) =>
                            item.itemType === 'header' ? item.title : item.paper.id
                        }
                        estimatedItemSize={200}
                        showsVerticalScrollIndicator={false}
                        ListHeaderComponent={ListHeaderComponent}
                        ListFooterComponent={ListFooterComponent}
                        onEndReached={() => {
                            if (hasNextPage && !isFetchingNextPage) {
                                fetchNextPage();
                            }
                        }}
                        onEndReachedThreshold={0.5}
                        recycleItems={true}
                        contentContainerStyle={styles.listContent}
                        style={styles.list}
                    />
                )}
                
                {/* ScrollView on top (fades out during transition) */}
                {(!isHydrated || isTransitioning) && (
                    <View style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: scrollViewOpacity,
                        backgroundColor: '#fafafa',
                    }}>
                        <ScrollView
                            style={styles.list}
                            contentContainerStyle={styles.listContent}
                            showsVerticalScrollIndicator={false}
                            scrollEventThrottle={16}
                            removeClippedSubviews={false}
                        >
                            <ListHeaderComponent />
                            {listData.map((item) => (
                                <View key={item.itemType === 'header' ? `header-${item.title}` : `paper-${item.paper.id}`}>
                                    {renderItem({ item })}
                                </View>
                            ))}
                            <Footer />
                        </ScrollView>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#fafafa',
    },
    archiveNotice: {
        marginHorizontal: 20,
        marginTop: 40,
        marginBottom: 30,
        padding: 20,
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.paleGreen,
    },
    archiveNoticeText: {
        fontSize: 15,
        lineHeight: 22,
        color: '#374151',
    },
    footerLoader: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    loadingMoreText: {
        marginTop: 8,
        fontSize: 14,
        color: colors.primaryGreenDark,
    },
    list: {
        flex: 1,
    },
    listContent: {
        flexGrow: 1,
        paddingBottom: 20,
    },
    contentWrapper: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
    },
    resultsTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 20,
        paddingHorizontal: 20,
    },
    paperWrapper: {
        paddingHorizontal: 20,
        marginBottom: 8,
    },
    confirmationBanner: {
        backgroundColor: '#f0fdf4',
        borderBottomWidth: 1,
        borderTopWidth: 1,
        borderBottomColor: colors.veryLightGreen,
        borderTopColor: colors.veryLightGreen,
        paddingVertical: 30,
        paddingHorizontal: 20,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    confirmationContent: {
        flex: 1,
        marginRight: 14,
    },
    confirmationTitle2: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 2,
    },
    confirmationText: {
        fontSize: 14,
        color: '#4b5563',
    },
    confirmationClose: {
        padding: 4,
    },
    confirmationCloseText: {
        fontSize: 18,
        color: '#6b7280',
        fontWeight: '500',
    },
    connectionNoticeCard: {
        marginHorizontal: 20,
        marginBottom: 20,
        backgroundColor: '#f0fdf4',
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: colors.paleGreen,
    },
    connectionNoticeContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    connectionNoticeInfo: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    connectionNoticeIcon: {
        color: colors.primaryGreen,
        marginRight: 10,
    },
    connectionNoticeText: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.primaryGreenDark,
    },
    refreshButton: {
        padding: 4,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        paddingHorizontal: 20,
        flexWrap: 'wrap',
        rowGap: 8,
    },
    headerRowTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        flexShrink: 0,
    },
    browseButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderWidth: 1,
        borderColor: colors.primaryGreenDark,
        borderRadius: 6,
        backgroundColor: 'transparent',
    },
    browseButtonText: {
        color: colors.primaryGreenDark,
        fontSize: 14,
        fontWeight: '500',
    },
})
